import { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspaces } from './useWorkspaces';
import { supabase } from '@/lib/supabase';
import { useToast } from './use-toast';
import { captureError } from '@/lib/sentry';

export interface WorkspaceSettings {
  skip_options?: Array<{ days: number; label: string }>;
  theme?: string;
  default_view?: string;
  email_signature?: string;
  contact_view_preferences?: {
    columnsToShow: string[];
    defaultSort: string;
    defaultSortDirection: 'asc' | 'desc';
  };
  call_thresholds?: Array<{ minutes: number; color: string }>;
}

export function useWorkspaceSettings() {
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentWorkspace, loading: workspaceLoading } = useWorkspaces();
  const { toast } = useToast();
  
  // Add a ref to prevent excessive fetches and flickering
  const fetchedRef = useRef(false);
  const fetchAttemptsRef = useRef(0);
  const maxFetchAttempts = 3;

  const fetchSettings = useCallback(async (force = false) => {
    if (!currentWorkspace?.id) {
      setSettings(null);
      setLoading(false);
      return;
    }

    if (fetchedRef.current && !force) {
      return; // Skip fetch if already fetched once, unless forced
    }

    try {
      setLoading(true);
      console.log('[useWorkspaceSettings] Fetching workspace settings for workspace:', currentWorkspace.id);
      
      const { data, error } = await supabase
        .from('workspace_settings')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .maybeSingle();

      if (error) throw error;

      const workspaceSettings = data || {
        workspace_id: currentWorkspace.id,
        skip_options: [
          { days: 7, label: '1 week' },
          { days: 14, label: '2 weeks' },
          { days: 30, label: '1 month' },
          { days: 60, label: '2 months' },
          { days: 90, label: '3 months' },
          { days: 180, label: '6 months' },
          { days: 360, label: '1 year' },
        ]
      };

      console.log('[useWorkspaceSettings] Settings fetched successfully:', workspaceSettings);
      setSettings(workspaceSettings);
      fetchedRef.current = true;
      fetchAttemptsRef.current = 0;
    } catch (error) {
      console.error('[useWorkspaceSettings] Error fetching workspace settings:', error);
      fetchAttemptsRef.current += 1;
      
      // Only show toast if we've tried multiple times
      if (fetchAttemptsRef.current >= maxFetchAttempts) {
        captureError(error as Error, { feature: 'workspace-settings-fetch', retriesExhausted: true });
        toast({
          title: "Warning",
          description: "Unable to load workspace settings, using defaults",
          variant: "destructive",
        });
      } else if (currentWorkspace?.id) {
        // Retry after a delay with backoff
        const delay = Math.min(1000 * Math.pow(2, fetchAttemptsRef.current - 1), 5000);
        console.log(`[useWorkspaceSettings] Retrying in ${delay}ms (attempt ${fetchAttemptsRef.current}/${maxFetchAttempts})`);
        
        setTimeout(() => {
          if (currentWorkspace?.id) { // Double check workspace still exists
            fetchSettings(true);
          }
        }, delay);
      }
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, toast]);

  const updateSettings = useCallback(async (newSettings: Partial<WorkspaceSettings>) => {
    if (!currentWorkspace?.id) return;

    try {
      console.log('[useWorkspaceSettings] Updating settings:', newSettings);
      const { data, error } = await supabase
        .from('workspace_settings')
        .upsert({
          workspace_id: currentWorkspace.id,
          ...newSettings,
        }, {
          onConflict: 'workspace_id'
        })
        .select();

      if (error) throw error;
      // RLS on workspace_settings restricts modify to owner/admin. A non-admin
      // upsert returns no error but zero rows; surface that as a permission
      // failure rather than letting the success toast lie.
      if (!data || data.length === 0) {
        throw new Error('You do not have permission to update workspace settings.');
      }
      
      fetchSettings(true);

      toast({
        description: "Settings updated successfully",
      });
    } catch (error) {
      console.error('[useWorkspaceSettings] Error updating workspace settings:', error);
      captureError(error as Error, { feature: 'workspace-settings-update' });
      toast({
        title: "Error",
        description: "Failed to update settings",
        variant: "destructive",
      });
    }
  }, [currentWorkspace?.id, toast, fetchSettings]);

  // Wait for workspace to be loaded before fetching settings
  useEffect(() => {
    if (!workspaceLoading && currentWorkspace?.id && !fetchedRef.current) {
      console.log('[useWorkspaceSettings] Workspace loaded, fetching settings');
      fetchSettings();
    }
    
    // Reset fetched state if workspace changes
    if (currentWorkspace?.id) {
      return () => {
        fetchedRef.current = false;
      };
    }
  }, [currentWorkspace?.id, fetchSettings, workspaceLoading]);

  return {
    settings,
    loading,
    updateSettings,
    refetchSettings: () => fetchSettings(true)
  };
}